// ROOM 404: Client Web Push, PWA Install & Offline Resilience Service

import { apiRequest } from './api';

const INSTALL_DISMISSED_KEY = 'room404_install_prompt_dismissed_at';
const FOURTEEN_DAYS_MS = 14 * 24 * 60 * 60 * 1000;

export interface PushStatus {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  isIos: boolean;
  isStandalone: boolean;
  isSubscribed: boolean;
}

/**
 * Converts a base64url VAPID public key into a Uint8Array for pushManager.subscribe
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Inspects device, browser, and push support without triggering prompts.
 */
export function getDeviceAndPushSupport(): {
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  isIos: boolean;
  isStandalone: boolean;
} {
  const isBrowser = typeof window !== 'undefined';
  if (!isBrowser) {
    return { supported: false, permission: 'unsupported', isIos: false, isStandalone: false };
  }

  const isIos =
    /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true;

  const pushSupported =
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;

  const permission = 'Notification' in window ? Notification.permission : 'unsupported';

  return {
    supported: pushSupported,
    permission,
    isIos,
    isStandalone,
  };
}

/**
 * Checks if active push subscription exists in ServiceWorker Registration.
 */
export async function getActivePushSubscription(): Promise<PushSubscription | null> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/**
 * Checks if the backend server has Web Push VAPID keys properly configured.
 */
export async function isPushServerConfigured(): Promise<boolean> {
  try {
    const res = await apiRequest<{ publicKey: string }>('/api/push/public-key');
    return Boolean(res?.publicKey);
  } catch {
    return false;
  }
}

/**
 * Requests notification permission after explicit user gesture and registers subscription.
 * Verifies server VAPID key availability BEFORE requesting browser notification permission.
 */
export async function requestAndSubscribePush(options: {
  optInMissions?: boolean;
} = {}): Promise<{
  success: boolean;
  permission: NotificationPermission | 'unsupported';
  error?: string;
}> {
  const support = getDeviceAndPushSupport();
  if (!support.supported) {
    return {
      success: false,
      permission: 'unsupported',
      error: support.isIos && !support.isStandalone
        ? 'iOS requires installing ROOM 404 to your Home Screen before enabling notifications.'
        : 'Web Push notifications are not supported by this browser.',
    };
  }

  // 1. Fetch server VAPID public key FIRST to verify server push is available
  let vapidPublicKey: string;
  try {
    const keyRes = await apiRequest<{ publicKey: string }>('/api/push/public-key');
    if (!keyRes?.publicKey) {
      throw new Error('Push not configured');
    }
    vapidPublicKey = keyRes.publicKey;
  } catch {
    return {
      success: false,
      permission: support.permission,
      error: 'Web Push notifications are not configured on this server.',
    };
  }

  // 2. Request browser permission ONLY after server VAPID configuration is verified
  let permission: NotificationPermission;
  try {
    permission = await Notification.requestPermission();
  } catch {
    return { success: false, permission: 'denied', error: 'Failed to request notification permission' };
  }

  if (permission !== 'granted') {
    return {
      success: false,
      permission,
      error: permission === 'denied'
        ? 'Notification permission was denied. You can re-enable notifications in site settings.'
        : 'Notification permission prompt was dismissed.',
    };
  }

  // 3. Register push with ServiceWorker
  try {
    const reg = await navigator.serviceWorker.ready;
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

    let subscription = await reg.pushManager.getSubscription();
    if (!subscription) {
      subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as any,
      });
    }

    const subJson = subscription.toJSON();
    if (!subJson.endpoint || !subJson.keys?.p256dh || !subJson.keys?.auth) {
      throw new Error('Push subscription keys unavailable from browser');
    }

    // 4. Save subscription to server
    await apiRequest('/api/push/subscribe', {
      method: 'POST',
      body: {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys.p256dh,
          auth: subJson.keys.auth,
        },
        userAgent: navigator.userAgent,
        optInMissions: options.optInMissions ?? true,
      },
    });

    return { success: true, permission: 'granted' };
  } catch (err: any) {
    return {
      success: false,
      permission,
      error: err.message || 'Failed to activate push subscription on device',
    };
  }
}

/**
 * Unsubscribes active device push subscription.
 */
export async function unsubscribePush(): Promise<{ success: boolean; error?: string }> {
  try {
    const sub = await getActivePushSubscription();
    if (sub) {
      await sub.unsubscribe();
      await apiRequest('/api/push/unsubscribe', {
        method: 'POST',
        body: { endpoint: sub.endpoint },
      });
    }
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Failed to unsubscribe' };
  }
}

/**
 * Non-nagging PWA install tracking
 */
export function isInstallPromptSuppressed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  const dismissedAt = localStorage.getItem(INSTALL_DISMISSED_KEY);
  if (!dismissedAt) return false;
  const elapsed = Date.now() - Number(dismissedAt);
  return elapsed < FOURTEEN_DAYS_MS;
}

export function dismissInstallPrompt(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(INSTALL_DISMISSED_KEY, String(Date.now()));
}
